import { Injectable, inject } from '@angular/core'
import { EquipmentPagedResult, EquipmentsPaginatedQuery, GetPaginatedMaintenancesQuery, MaintenanceOrderByInput, MaintenancePagedResult } from '@generated/sammathnaur.graphql'
import { isEmpty } from 'lodash-es'
import { DetailsSectionServiceDispatchParams, DialogEquipmentTabsEnum, ToEventResult } from './details-section.types'
import { EquipmentsService } from '../../../../core/api/equipments/equipments.service'
import { MaintenancesService } from '../../../../core/api/maintenances/maintenances.service'
import { GlobalEventsService } from '../../../../core/services/global-events/global-events.service'
import { GlobalEventNameEnum } from '../../../../core/services/global-events/global-events.types'
import { TreeViewItemTypeEnum } from '../tree-section/components/tree-view/tree-view.type'


@Injectable({ providedIn: 'root' })
export class DetailsSectionService {
  public readonly globalEventsService = inject(GlobalEventsService)
  public readonly maintenancesService = inject(MaintenancesService)
  public readonly equipmentsService = inject(EquipmentsService)

  public dispatch({ id, item, type, reloadPage }: DetailsSectionServiceDispatchParams): void {
    const result = {} as ToEventResult

    result.type = GlobalEventNameEnum.OpenDialog
    result.data = {}

    if (type === 'addEnvironment') {
      result.data.dialog = 'locationEnvironment'
      result.data.location = item.original?.location
      result.data.locationSectorId = item.id
      result.data.isUpdatedFromParentDetails = true
    }

    if (type === 'addEquipment') {
      result.data.dialog = 'equipment'
      result.data.location = item.original
    }

    if (type === 'addSector') {
      result.data.dialog = 'locationSector'
      result.data.location = item.original
    }

    if (type === 'archive') {
      result.data.dialog = 'archive'
      result.data.locationSector = item.type === TreeViewItemTypeEnum.LocationSector ? item.original : item.original?.locationSector
      result.data.locationEnvironment = item.type === TreeViewItemTypeEnum.LocationEnvironment ? item.original : null
    }

    if (type === 'edit') {
      if (item.type === TreeViewItemTypeEnum.Location) {
        result.data.dialog = 'location'
        result.data.locationId = item.id
      }

      if (item.type === TreeViewItemTypeEnum.LocationSector) {
        result.data.dialog = 'locationSector'
        result.data.locationSectorId = item.id
      }

      if (item.type === TreeViewItemTypeEnum.LocationEnvironment) {
        result.data.dialog = 'locationEnvironment'
        result.data.location = item.original?.locationSector?.location
        result.data.locationSectorId = item.original?.locationSector?.id
        result.data.locationEnvironmentId = item.id
      }

      if (item.type === TreeViewItemTypeEnum.Equipment) {
        result.data.dialog = 'equipment'
        result.data.equipmentId = item.id
      }
    }

    if (type === 'linkEquipment') {
      result.data.dialog = 'linkEquipment'
      result.data.locationId = item.original?.location?.id || item.original.locationSector?.location?.id
      result.data.locationSector = item.type === TreeViewItemTypeEnum.LocationSector ? item.original : item.original?.locationSector
      result.data.locationEnvironment = item.type === TreeViewItemTypeEnum.LocationEnvironment ? item.original : null
    }

    if (type === 'viewEquipment') {
      result.data.dialog = 'equipment'
      result.data.equipmentId = id
      result.data.reloadPage = reloadPage
      result.data.isUpdatedFromParentDetails = true
    }

    if (type === 'viewEquipmentWarranties') {
      result.data.dialog = 'equipment'
      result.data.equipmentId = id
      result.data.selectedTab = DialogEquipmentTabsEnum.WARRANTIES
      result.data.reloadPage = reloadPage
      result.data.isUpdatedFromParentDetails = true
    }

    if (type === 'viewMaintenance') {
      result.data.dialog = 'maintenance'
      result.data.reloadPage = reloadPage
      result.data.maintenanceId = id
    }

    if (!isEmpty(result.data)) {
      this.globalEventsService.dispatchEvent(result.type as GlobalEventNameEnum, result.data)
    }
  }

  public async loadMaintenances(item: any): Promise<MaintenancePagedResult> {
    this.setMaintenancesFilter(item, 3)
    const result = await this.maintenancesService.searchPage()
    return (result.data as GetPaginatedMaintenancesQuery)?.maintenances as MaintenancePagedResult
  }

  public async loadEquipments(item: any): Promise<EquipmentPagedResult> {
    this.setEquipmentsFilter(item, 3)
    const result = await this.equipmentsService.searchPage()
    return (result.data as EquipmentsPaginatedQuery)?.equipments as EquipmentPagedResult
  }

  public setMaintenancesFilter(item: any, limit: number): void {
    this.maintenancesService.reset()
    this.maintenancesService.limit.set(limit)
    this.maintenancesService.filterForm.get('archived')?.setValue(false)
    this.maintenancesService.orderBy.set(MaintenanceOrderByInput.OpenedAtDesc)

    if (item?.type === TreeViewItemTypeEnum.Equipment) {
      this.maintenancesService.filterForm.get('equipmentId_in')?.setValue([item.id])
    }

    if (item?.type === TreeViewItemTypeEnum.Location) {
      this.maintenancesService.filterForm.get('locationId_in')?.setValue([item.id])
    }

    if (item?.type === TreeViewItemTypeEnum.LocationSector) {
      this.maintenancesService.filterForm.get('locationSectorId_in')?.setValue([item.id])
    }

    if (item?.type === TreeViewItemTypeEnum.LocationEnvironment) {
      this.maintenancesService.filterForm.get('locationEnvironmentId_in')?.setValue([item.id])
    }
  }

  public setEquipmentsFilter(item: any, limit: number): void {
    this.equipmentsService.reset()
    this.equipmentsService.limit.set(limit)
    this.equipmentsService.filterForm.get('archived')?.setValue('all')

    if (item?.type === TreeViewItemTypeEnum.Location) {
      this.equipmentsService.filterForm.get('locationId_in')?.setValue([item.id])
    }

    if (item?.type === TreeViewItemTypeEnum.LocationSector) {
      this.equipmentsService.filterForm.get('locationSectorId_in')?.setValue([item.id])
    }

    if (item?.type === TreeViewItemTypeEnum.LocationEnvironment) {
      this.equipmentsService.filterForm.get('locationEnvironmentId_in')?.setValue([item.id])
    }
  }
}
